'use client'

import React from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Download,
  MapPin,
  Envelope,
  LinkedinLogo,
  Briefcase,
  GraduationCap,
  Rocket,
  UsersThree,
  DeviceMobile,
  CurrencyDollar,
  Trophy,
  Bug,
  Star,
  Lightning,
  Buildings,
  Clock,
  Brain,
} from 'phosphor-react'
import { Link } from '@/lib'
import Components from '../components'
import { CV_PDF_DOWNLOAD_NAME, CV_PDF_URL } from '@/utils/downloadCv'

export default function About() {
  return (
    <div className="min-h-screen relative overflow-hidden transition-none">
      
      <Components.GlassNavigation />
      
      <main className="pt-16 md:pt-32 pb-16 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-6 md:gap-12">
            {/* Left Sidebar - Profile Card */}
            <div className="md:w-1/3 md:flex-shrink-0 md:min-w-0">
              <Card className="brutal-panel p-4 md:p-8 text-center md:sticky md:top-32">
                <Avatar className="w-32 h-32 mx-auto mb-6 ring-4 ring-white/20">
                  <AvatarImage src="https://wemxiqpkiyrwpffrdrgu.supabase.co/storage/v1/object/public/public-assets/projects/c0329852-5824-4c56-b2fb-026919af3589/a449770d-d689-4eef-b810-94ffbacd1921.jpeg" alt="Dixon Bordiano" className="object-cover" />
                  <AvatarFallback className="text-2xl bg-[var(--brutal-mustard)] border-2 border-[var(--brutal-ink)] text-[var(--brutal-ink)]">DB</AvatarFallback>
                </Avatar>
                
                <h1 className="text-2xl font-bold text-[var(--brutal-ink)] mb-2">Dixon Bordiano</h1>
                <p className="text-xl text-[var(--brutal-muted)] mb-4">Creative & Product Design Leader</p>
                
                <div className="flex items-center justify-center text-[var(--brutal-muted)] mb-6">
                  <MapPin weight="duotone" className="w-4 h-4 mr-2" />
                  Porto, Portugal
                </div>
                
                <div className="space-y-3 mb-8">
                  <Button 
                    variant="outline" 
                    className="w-full bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] hover:text-[var(--brutal-ink)]"
                    asChild
                  >
                    <a href="mailto:dbordiano@gmail.com">
                      <Envelope weight="duotone" className="w-4 h-4 mr-2" />
                      dbordiano@gmail.com
                    </a>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] hover:text-[var(--brutal-ink)]"
                    asChild
                  >
                    <a href="https://www.linkedin.com/in/dixonbordiano/" target="_blank" rel="noopener noreferrer">
                      <LinkedinLogo weight="duotone" className="w-4 h-4 mr-2" />
                      linkedin.com/in/dixonbordiano
                    </a>
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <Button 
                    asChild
                    variant="outline" 
                    className="w-full bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] hover:text-[var(--brutal-ink)] transition-colors cursor-pointer"
                  >
                    <Link to="/Resume">View Full Resume</Link>
                  </Button>
                  
                  <Button 
                    asChild
                    className="w-full brutal-btn text-[var(--brutal-ink)] border-0 px-4 py-2 rounded-md font-medium flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <a href={CV_PDF_URL} download={CV_PDF_DOWNLOAD_NAME}>
                      <Download weight="duotone" className="w-4 h-4 mr-2" />
                      Download CV
                    </a>
                  </Button>
                </div>
              </Card>
            </div>
            
            {/* Right Content Area */}
            <div className="md:w-2/3 md:flex-grow md:min-w-0">
              <Tabs defaultValue="about" className="space-y-8">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="about">About</TabsTrigger>
                  <TabsTrigger value="experience">Experience</TabsTrigger>
                  <TabsTrigger value="achievements">Achievements</TabsTrigger>
                </TabsList>
                
                <TabsContent value="about" className="space-y-8">
                  <Card className="brutal-panel p-8">
                    <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-6">About Me</h2>
                    <div className="space-y-6 text-[var(--brutal-muted)] leading-relaxed">
                      <p>
                        I'm a transformational UX and product operations leader with 12+ years of experience in enterprise SaaS, AI-powered products, and design leadership. My journey began with a foundation in Studio Art (Digital Art) from the University of Minnesota Morris, which evolved into a passion for solving complex enterprise challenges through human-centered design.
                      </p>
                      <p>
                        Throughout my career, I've built and scaled UX practices from the ground up — including founding Cognira's first in-house Product Design & UX practice. I've had the privilege of establishing the Portugal UX office, growing client bases by 400%, and driving over $6M in new ARR through strategic design decisions and data-driven product operations. I'm now Co-Founder at URSO Invencivel.
                      </p>
                      <p>
                        My approach integrates human-centered design with data-driven product operations, focusing on metrics frameworks that increase product adoption, team velocity, and customer satisfaction. I'm particularly passionate about AI-powered products and design system governance, where systematic thinking meets innovative user experiences.
                      </p>
                    </div>
                  </Card>
                  
                  <Card className="brutal-panel p-8">
                    <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-6">Core Competencies</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Design Leadership</h4>
                          <p className="text-[var(--brutal-muted)] text-sm">Strategic vision, team management, design culture, mentorship</p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">User Research</h4>
                          <p className="text-[var(--brutal-muted)] text-sm">User interviews, usability testing, behavioral analysis, persona development</p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Enterprise UX</h4>
                          <p className="text-[var(--brutal-muted)] text-sm">Complex workflows, B2B platforms, system integration, scalability</p>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Design Systems</h4>
                          <p className="text-[var(--brutal-muted)] text-sm">Component libraries, design tokens, governance, documentation</p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Product Strategy</h4>
                          <p className="text-[var(--brutal-muted)] text-sm">Roadmap planning, feature prioritization, stakeholder alignment</p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Data-Driven Design</h4>
                          <p className="text-[var(--brutal-muted)] text-sm">Analytics, A/B testing, performance metrics, ROI measurement</p>
                        </div>
                      </div>
                    </div>
                  </Card>

                  <Card className="brutal-panel p-8">
                    <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-6">Tools</h3>
                    <div className="space-y-6">
                      {([
                        {
                          label: 'Design',
                          tools: [
                            { name: 'Figma', level: 5 },
                            { name: 'Adobe Photoshop', level: 4 },
                            { name: 'Adobe Illustrator', level: 4 },
                            { name: 'Adobe XD', level: 4 },
                            { name: 'Adobe After Effects', level: 3 },
                            { name: 'Sketch', level: 3 },
                            { name: 'Framer', level: 3 },
                            { name: 'Lovable', level: 2 },
                            { name: 'Claude Design', level: 3 },
                            { name: 'OpenDesign', level: 2 },
                            { name: 'Pencil', level: 2 },
                            { name: 'Axure', level: 4 },
                          ],
                        },
                        {
                          label: 'Workshop & facilitation',
                          tools: [
                            { name: 'FigJam', level: 5 },
                            { name: 'Miro', level: 4 },
                          ],
                        },
                        {
                          label: 'Research',
                          tools: [
                            { name: 'UserTesting', level: 4 },
                            { name: 'Dovetail', level: 4 },
                            { name: 'Sentry', level: 3 },
                            { name: 'Grafana', level: 2 },
                            { name: 'Looker Studio', level: 3 },
                            { name: 'Optimal', level: 3 },
                            { name: 'Maze', level: 3 },
                            { name: 'Zoom', level: 5 },
                          ],
                        },
                        {
                          label: 'Build',
                          tools: [
                            { name: 'Zed', level: 3 },
                            { name: 'VS Code', level: 4 },
                            { name: 'Docker', level: 3 },
                            { name: 'Java', level: 4 },
                            { name: 'JavaScript', level: 4 },
                            { name: 'TypeScript', level: 4 },
                            { name: 'React', level: 4 },
                            { name: 'CSS + HTML', level: 5 },
                            { name: 'Python', level: 3 },
                            { name: 'Gradle', level: 2 },
                            { name: 'Swift', level: 2 },
                            { name: 'Make', level: 3 },
                            { name: 'Playwright', level: 3 },
                            { name: 'Supabase', level: 4 },
                            { name: 'Neon', level: 3 },
                            { name: 'Cloudflare', level: 3 },
                            { name: 'Vercel', level: 3 },
                            { name: 'Netlify', level: 4 },
                            { name: 'Next.js', level: 4 },
                            { name: 'Prisma', level: 3 },
                            { name: 'PostgreSQL', level: 3 },
                            { name: 'Tailwind CSS', level: 4 },
                            { name: 'Storybook', level: 5 },
                            { name: 'Vitest', level: 3 },
                            { name: 'Expo', level: 2 },
                            { name: 'Godot', level: 2 },
                            { name: 'Capacitor', level: 2 },
                            { name: 'Electron', level: 2 },
                            { name: 'GitHub', level: 4 },
                            { name: 'Git', level: 4 },
                            { name: 'Resend', level: 3 },
                            { name: 'Shopify', level: 3 },
                            { name: 'Mollie', level: 3 },
                            { name: 'Keycloak', level: 2 },
                          ],
                        },
                        {
                          label: 'Product & ops',
                          tools: [
                            { name: 'Jira', level: 5 },
                            { name: 'Linear', level: 4 },
                            { name: 'Confluence', level: 5 },
                            { name: 'Notion', level: 4 },
                            { name: 'Google Analytics', level: 3 },
                            { name: 'Umami', level: 3 },
                            { name: 'Wiki.js', level: 3 },
                            { name: 'Nextcloud', level: 3 },
                            { name: 'OpenProject', level: 2 },
                          ],
                        },
                        {
                          label: 'AI',
                          tools: [
                            { name: 'Claude', level: 5 },
                            { name: 'Cursor', level: 5 },
                            { name: 'Codex', level: 3 },
                            { name: 'OpenCode', level: 2 },
                            { name: 'Antigravity', level: 2 },
                          ],
                        },
                      ] as const).map(({ label, tools }) => (
                        <div key={label}>
                          <h4 className="brutal-label mb-3">{label}</h4>
                          <div className="flex flex-wrap gap-2">
                            {tools.map(({ name, level }) => {
                              const pct = (level / 5) * 100
                              return (
                                <span
                                  key={name}
                                  title={`${name}: ${level}/5`}
                                  aria-label={`${name}, proficiency ${level} of 5`}
                                  className="relative inline-flex items-center overflow-hidden rounded-full border-2 border-[var(--brutal-ink)] bg-[var(--brutal-kraft)] shadow-[var(--brutal-shadow-sm)] px-3 py-1 text-sm font-medium text-[var(--brutal-ink)]"
                                >
                                  <span
                                    aria-hidden="true"
                                    className="absolute inset-y-0 left-0 bg-[var(--brutal-mustard)]"
                                    style={{ width: `${pct}%` }}
                                  />
                                  <span className="relative z-10">{name}</span>
                                </span>
                              )
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                </TabsContent>
                
                <TabsContent value="experience" className="space-y-8">
                  <Card className="brutal-panel p-8">
                    <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-6">Professional Journey</h2>
                    <div className="space-y-8">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 aspect-square bg-[#c4a7e7] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-full flex items-center justify-center flex-shrink-0">
                          <Rocket weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-[var(--brutal-ink)] mb-2">Co-Founder</h3>
                          <p className="text-[var(--brutal-muted)] mb-2">
                            <a href="https://ursoinvencivel.com" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--brutal-ink)] underline underline-offset-2">URSO Invencivel</a>
                            {' '}• Mar 2026 - Present
                          </p>
                          <p className="text-[var(--brutal-muted)] mb-4">
                            Co-founded URSO Invencivel with a focus on consulting, workshops, and education. Design and facilitate practical learning experiences; advise clients on strategy and process; stay involved while open to full-time, part-time, or fractional design leadership roles.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">Founding</Badge>
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">Consulting</Badge>
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">Workshops</Badge>
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">Facilitation</Badge>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 aspect-square bg-[var(--brutal-accent)] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-full flex items-center justify-center flex-shrink-0">
                          <UsersThree weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-[var(--brutal-ink)] mb-2">Director of UX</h3>
                          <p className="text-[var(--brutal-muted)] mb-2">Cognira • Mar 2023 - Jan 2026</p>
                          <p className="text-[var(--brutal-muted)] mb-4">
                            Founded Cognira&apos;s first in-house Product Design &amp; UX practice for PromoAI. Built and scaled a cross-continental team and the Portugal UX office; grew client base 400% and drove $6M+ in new ARR. Led accessibility-first design system (Figma + Storybook)—regression bugs –44%, usability fixes +50%—and introduced a usability-bug process for production quality.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">AI/ML Design</Badge>
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">Practice Building</Badge>
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">Design Systems</Badge>
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">Accessibility</Badge>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-[var(--brutal-mustard)] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-full flex items-center justify-center flex-shrink-0">
                          <Briefcase weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-[var(--brutal-ink)] mb-2">Senior UX Designer</h3>
                          <p className="text-[var(--brutal-muted)] mb-2">Oracle • Mar 2015 - Mar 2023</p>
                          <p className="text-[var(--brutal-muted)] mb-4">
                            Led multi-team UX initiatives across planning, supply chain, store operations, and analytics products. Embedded user research and usability testing into agile workflows. Recognized as Employee of the Year (2017) for leading cross-product UX unification.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">Enterprise UX</Badge>
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">Supply Chain</Badge>
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">User Research</Badge>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-[#7eb8da] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-full flex items-center justify-center flex-shrink-0">
                          <DeviceMobile weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-[var(--brutal-ink)] mb-2">UX Designer</h3>
                          <p className="text-[var(--brutal-muted)] mb-2">Digital River • Jun 2014 - Mar 2015</p>
                          <p className="text-[var(--brutal-muted)] mb-4">
                            Conducted user research and created interaction patterns for e-commerce applications. Designed enterprise mobile point-of-sale, customer service tools, and checkout flows.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">E-commerce</Badge>
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">Mobile Design</Badge>
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">User Research</Badge>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-[#8fce8f] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-full flex items-center justify-center flex-shrink-0">
                          <GraduationCap weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-[var(--brutal-ink)] mb-2">B.A. Studio Art (Digital Art)</h3>
                          <p className="text-[var(--brutal-muted)] mb-2">University of Minnesota Morris • 2010</p>
                          <p className="text-[var(--brutal-muted)] mb-4">
                            Foundation in visual design, composition, and creative problem-solving that formed the basis for a career in user experience design.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">Visual Design</Badge>
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">Creative Arts</Badge>
                            <Badge className="bg-[var(--brutal-panel)] text-[var(--brutal-muted)] border border-[var(--brutal-ink)]">Design Foundation</Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </TabsContent>
                
                <TabsContent value="achievements" className="space-y-8">
                  <Card className="brutal-panel p-8">
                    <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-6">Key Achievements</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-6">
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 aspect-square bg-[#8fce8f] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-full flex items-center justify-center flex-shrink-0">
                            <CurrencyDollar weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-1">$6M+ New ARR Generated</h4>
                            <p className="text-[var(--brutal-muted)] text-sm">Established Portugal UX office and grew client base by 400%, driving significant new annual recurring revenue</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 bg-[#7eb8da] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-full flex items-center justify-center flex-shrink-0">
                            <Bug weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-1">44% Reduction in Regression Bugs</h4>
                            <p className="text-[var(--brutal-muted)] text-sm">Built Storybook code framework and integrated design system with engineering, significantly improving product quality</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 bg-[var(--brutal-accent)] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-full flex items-center justify-center flex-shrink-0">
                            <Lightning weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-1">50% Faster Usability Fixes</h4>
                            <p className="text-[var(--brutal-muted)] text-sm">Improved design system integration and processes, accelerating time to market for usability improvements</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 bg-[#8fce8f] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-full flex items-center justify-center flex-shrink-0">
                            <Clock weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-1">60% Reduction in Training Time</h4>
                            <p className="text-[var(--brutal-muted)] text-sm">Streamlined user onboarding and reduced help desk tickets through improved UX design and documentation</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-6">
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 bg-[#f4a6b8] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-full flex items-center justify-center flex-shrink-0">
                            <Buildings weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-1">Founded First UX Practice</h4>
                            <p className="text-[var(--brutal-muted)] text-sm">Established Cognira's inaugural in-house Product Design & UX practice for PromoAI SaaS solution</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 bg-[#c4a7e7] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-full flex items-center justify-center flex-shrink-0">
                            <Star weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-1">CIO Star Award (2012)</h4>
                            <p className="text-[var(--brutal-muted)] text-sm">Early career recognition at Digital River for contributions to QA automation and testing initiatives</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 bg-[#7eb8da] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-full flex items-center justify-center flex-shrink-0">
                            <Brain weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-1">AI/ML Design Leadership</h4>
                            <p className="text-[var(--brutal-muted)] text-sm">Directed Agentic AI UX vision and used design thinking to develop a comprehensive forecast analysis experience for Predictive AI products</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 bg-[var(--brutal-mustard)] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-full flex items-center justify-center flex-shrink-0">
                            <Trophy weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-1">Employee of the Year (2017)</h4>
                            <p className="text-[var(--brutal-muted)] text-sm">Recognized at Oracle for leading cross-product UX unification and outstanding contribution to enterprise design</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}